/**
 * @fileoverview Componente responsável por exibir informações e ações relacionadas à carteira.
 */

import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Box,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PersonIcon from "@material-ui/icons/Person";
import FolderSharedIcon from "@material-ui/icons/FolderShared";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission from "../../hooks/usePermission";

import { ChevronDown, X } from "lucide-react";
const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      minWidth: 500,
      maxWidth: 700,
      background:
        theme.palette.type === "dark"
          ? "linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)"
          : "#ffffff",
    },
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 3),
    background:
      theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.1)"
        : "rgba(61, 92, 253, 0.05)",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(91, 124, 255, 0.2)"
        : "1px solid rgba(0, 0, 0, 0.08)",
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  titleIcon: {
    color: theme.palette.primary.main,
    fontSize: 28,
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
    maxHeight: 500,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  accordion: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(0, 0, 0, 0.02)",
    borderRadius: "12px !important",
    marginBottom: theme.spacing(1.5),
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(91, 124, 255, 0.1)"
        : "1px solid rgba(0, 0, 0, 0.06)",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: theme.spacing(0, 0, 1.5, 0),
    },
  },
  accordionSummary: {
    minHeight: 56,
    "&.Mui-expanded": {
      minHeight: 56,
    },
  },
  userInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    width: "100%",
  },
  userAvatar: {
    width: 40,
    height: 40,
    background: theme.palette.primary.main,
  },
  userName: {
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#fff" : "#1e293b",
  },
  contactCount: {
    marginLeft: "auto",
    marginRight: theme.spacing(1),
  },
  contactList: {
    padding: 0,
    width: "100%",
  },
  contactItem: {
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    paddingRight: 90,
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.08)"
          : "rgba(61, 92, 253, 0.04)",
    },
  },
  contactAvatar: {
    width: 36,
    height: 36,
  },
  contactName: {
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  contactNumber: {
    fontSize: "0.8rem",
    color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
  },
  actionButton: {
    padding: 6,
  },
  transferIcon: {
    fontSize: 20,
    color: theme.palette.primary.main,
  },
  removeIcon: {
    fontSize: 20,
    color: theme.palette.type === "dark" ? "#f87171" : "#ef4444",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 180,
  },
  menuAvatar: {
    width: 28,
    height: 28,
    fontSize: 14,
    background: theme.palette.primary.main,
  },
}));

const WalletModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { user: currentUser } = useContext(AuthContext);
  // Transferir e tirar da carteira: hoje, abertos a quem atende; com a regra
  // nova, contacts.wallet_manage.
  const { can } = usePermission();
  const canManageWallet = can("contacts.wallet_manage", true);
  const [walletUsers, setWalletUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  // Menu de transferência
  const [transferAnchor, setTransferAnchor] = useState(null);
  const [transferContact, setTransferContact] = useState(null);
  const [transferFromUser, setTransferFromUser] = useState(null);

  useEffect(() => {
    if (open) {
      fetchData(canManageWallet);
    }
  }, [open, canManageWallet]);

  // A lista de usuários só serve para transferir: sem poder, não é pedida.
  const fetchData = async (withUsers) => {
    setLoading(true);
    try {
      const [walletRes, usersRes] = await Promise.all([
        api.get("/contacts/wallet"),
        withUsers ? api.get("/users/list") : Promise.resolve({ data: [] }),
      ]);
      setWalletUsers(walletRes.data.walletUsers || []);
      setAllUsers(usersRes.data || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleClose = () => {
    setExpanded(false);
    setTransferAnchor(null);
    setTransferContact(null);
    setTransferFromUser(null);
    onClose();
  };

  const handleRemoveFromWallet = async (contactId, contactName) => {
    try {
      await api.put(`/contacts/${contactId}/wallet`, { walletUserId: null });
      toast.success(`${contactName} removido da carteira`);
      fetchData(canManageWallet);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTransferMenu = (event, contact, fromUserId) => {
    event.stopPropagation();
    setTransferAnchor(event.currentTarget);
    setTransferContact(contact);
    setTransferFromUser(fromUserId);
  };

  const handleCloseTransferMenu = () => {
    setTransferAnchor(null);
    setTransferContact(null);
    setTransferFromUser(null);
  };

  const handleTransfer = async (toUserId, toUserName) => {
    if (!transferContact) return;

    try {
      await api.put(`/contacts/${transferContact.id}/wallet`, {
        walletUserId: toUserId,
      });
      toast.success(
        `${transferContact.name} transferido para ${toUserName}`
      );
      handleCloseTransferMenu();
      fetchData(canManageWallet);
    } catch (err) {
      toastError(err);
    }
  };

  // Usuários disponíveis para transferência (exclui o dono atual)
  const transferOptions = allUsers.filter(
    (u) => u.id !== transferFromUser
  );

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <div className={classes.dialogTitle}>
        <div className={classes.titleWrapper}>
          <FolderSharedIcon className={classes.titleIcon} />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Carteira de Clientes
          </Typography>
        </div>
        <IconButton size="small" onClick={handleClose}>
          <X />
        </IconButton>
      </div>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : walletUsers.length === 0 ? (
          <Box className={classes.emptyState}>
            <FolderSharedIcon className={classes.emptyIcon} />
            <Typography variant="body1">
              Nenhum cliente na carteira ainda
            </Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              Quando um atendente aceitar um ticket, o cliente será
              automaticamente adicionado à sua carteira.
            </Typography>
          </Box>
        ) : (
          walletUsers.map((user) => (
            <Accordion
              key={user.id}
              className={classes.accordion}
              expanded={expanded === user.id}
              onChange={handleAccordionChange(user.id)}
            >
              <AccordionSummary
                expandIcon={<ChevronDown />}
                className={classes.accordionSummary}
              >
                <div className={classes.userInfo}>
                  <Avatar className={classes.userAvatar}>
                    <PersonIcon />
                  </Avatar>
                  <Typography className={classes.userName}>
                    {user.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${user.contacts.length} cliente${
                      user.contacts.length !== 1 ? "s" : ""
                    }`}
                    color="primary"
                    variant="outlined"
                    className={classes.contactCount}
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <List className={classes.contactList} dense>
                  {user.contacts.map((contact) => (
                    <ListItem
                      key={contact.id}
                      className={classes.contactItem}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={contact.profilePicUrl}
                          className={classes.contactAvatar}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography className={classes.contactName}>
                            {contact.name}
                          </Typography>
                        }
                        secondary={
                          <Typography className={classes.contactNumber}>
                            {contact.number}
                          </Typography>
                        }
                      />
                      {canManageWallet && (
                        <ListItemSecondaryAction>
                          <Tooltip title="Transferir para outro atendente">
                            <IconButton
                              className={classes.actionButton}
                              onClick={(e) =>
                                handleOpenTransferMenu(e, contact, user.id)
                              }
                            >
                              <SwapHorizIcon
                                className={classes.transferIcon}
                              />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remover da carteira">
                            <IconButton
                              className={classes.actionButton}
                              onClick={() =>
                                handleRemoveFromWallet(
                                  contact.id,
                                  contact.name
                                )
                              }
                            >
                              <RemoveCircleOutlineIcon
                                className={classes.removeIcon}
                              />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </DialogContent>

      {/* Menu de transferência */}
      <Menu
        anchorEl={transferAnchor}
        open={Boolean(transferAnchor)}
        onClose={handleCloseTransferMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {transferOptions.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">
              Nenhum outro atendente disponível
            </Typography>
          </MenuItem>
        ) : (
          transferOptions.map((u) => (
            <MenuItem
              key={u.id}
              onClick={() => handleTransfer(u.id, u.name)}
              className={classes.menuItem}
            >
              <Avatar className={classes.menuAvatar}>
                {u.name?.charAt(0)?.toUpperCase()}
              </Avatar>
              <Typography variant="body2">{u.name}</Typography>
            </MenuItem>
          ))
        )}
      </Menu>
    </Dialog>
  );
};

export default WalletModal;
